import { Loader2, Plus, Search, Star } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { AgentSessionDTO, RosterItem } from '@/services/roros/agent-sessions.service';
import { SessionListItem } from './AgentChatPageParts';

interface AgentChatSidebarProps {
  selectedActorId: string;
  onSelectedActorIdChange: (actorId: string) => void;
  actorOptions: Array<{ value: string; label: string }>;
  roster: RosterItem[];
  onStartSession: () => void;
  startError: string | null;
  sidebarSearch: string;
  onSidebarSearchChange: (value: string) => void;
  onOpenDetailedSearch: () => void;
  isLoadingSessions: boolean;
  sessions: AgentSessionDTO[];
  activeSessionId: string | null;
  onOpenSession: (session: AgentSessionDTO) => void;
}

export function AgentChatSidebar({
  selectedActorId,
  onSelectedActorIdChange,
  actorOptions,
  roster,
  onStartSession,
  startError,
  sidebarSearch,
  onSidebarSearchChange,
  onOpenDetailedSearch,
  isLoadingSessions,
  sessions,
  activeSessionId,
  onOpenSession,
}: AgentChatSidebarProps) {
  const selectedActor = selectedActorId
    ? roster.find((r) => r.actorId === selectedActorId)
    : null;

  return (
    <aside className="w-64 flex-shrink-0 flex flex-col gap-2" data-testid="session-sidebar">
      {/* AC-AGS-M4-3: Actor picker from roster */}
      <div className="space-y-2 pb-2 border-b border-slate-200">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">{t('AI Operator')}</label>
          <Select
            value={selectedActorId}
            onValueChange={(value) => {
              if (value === '__none__') onSelectedActorIdChange('');
              else onSelectedActorIdChange(value);
            }}
            options={[
              { value: '__none__', label: t('— select —') },
              ...actorOptions,
            ]}
            placeholder={t('— select —')}
            data-testid="agent-chat-actor-select"
          />
          {/* Show routing description for selected actor */}
          {selectedActor && (
            <div className="space-y-1" data-testid="actor-routing-info">
              {selectedActor.isCoordinator && (
                <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full" data-testid="coordinator-badge">
                  <Star className="h-3 w-3" />
                  {t('Coordinator')}
                </span>
              )}
              {selectedActor.routingDescription && (
                <p className="text-xs text-slate-500 leading-relaxed" data-testid="routing-description">
                  {selectedActor.routingDescription}
                </p>
              )}
            </div>
          )}
        </div>
        <Button
          onClick={onStartSession}
          disabled={!selectedActorId}
          size="sm"
          className="w-full"
          data-testid="agent-chat-start-button"
        >
          <Plus className="h-4 w-4 mr-1" />
          {t('New Session')}
        </Button>
        {startError && (
          <p className="text-xs text-red-600" data-testid="agent-chat-start-error">{startError}</p>
        )}
      </div>

      {/* AC-CHAT-7: session search + detailed search link */}
      <div className="space-y-1">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <Input
            value={sidebarSearch}
            onChange={(e) => onSidebarSearchChange(e.target.value)}
            placeholder={t('Search sessions…')}
            className="h-7 pl-7 pr-2 text-xs"
            data-testid="session-search-input"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 h-6 px-2 justify-start"
          onClick={onOpenDetailedSearch}
          data-testid="session-detailed-search-btn"
        >
          <Search className="h-3 w-3 mr-1 flex-shrink-0" />
          {t('Detailed search')}
        </Button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto space-y-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-testid="session-list">
        {isLoadingSessions && (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        )}
        {!isLoadingSessions && sessions.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4" data-testid="session-list-empty">
            {t('No sessions yet')}
          </p>
        )}
        {sessions.map((session) => (
          <SessionListItem
            key={session.id}
            session={session}
            isActive={activeSessionId === session.id}
            onClick={() => onOpenSession(session)}
          />
        ))}
      </div>
    </aside>
  );
}
